import React from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import { useTheme } from "../../../../../contexts/ThemeContext";
import { 
  INPUT_BASE, 
  SELECT_BASE, 
  LABEL_BASE,
  CARD_SPACING 
} from "../../../../../constants/styles";

export default function OverviewEditModal({
  isOpen,
  onClose,
  maritalStatusTypes,
  taxFilingStatusOptions,
  filingStateOptions,
  onFieldChange,
  formData,
  familyCount
}) {
  const { isDarkMode } = useTheme();

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative z-[10000]">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Edit Household Overview
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Family Members Count - Read Only */}
          <div>
            <label className={LABEL_BASE}>Family Members</label>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              {`${familyCount.total} (${familyCount.dependents} dependents)`}
            </div>
          </div>

          {/* Marital Status */}
          <div>
            <label className={LABEL_BASE}>Marital Status</label>
            <select
              value={formData.personalInfo.maritalStatus}
              onChange={(e) => onFieldChange("maritalStatus", e.target.value)}
              className={`${SELECT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
            >
              <option value="">Select...</option>
              {maritalStatusTypes?.map((status) => (
                <option
                  key={status.marital_status_id}
                  value={status.marital_status_id}
                >
                  {status.marital_status_name}
                </option>
              ))}
            </select>
          </div>

          {/* Marriage Information */}
          <div>
            <label className={LABEL_BASE}>Marriage Information</label>
            <div className={`grid grid-cols-3 ${CARD_SPACING.grid}`}>
              <input
                type="date"
                value={formData.marriageInfo.marriageDate || ""}
                onChange={(e) =>
                  onFieldChange(
                    "marriageInfo",
                    "marriageDate",
                    "",
                    e.target.value,
                  )
                }
                className={`${INPUT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
              />
              <input
                type="text"
                value={formData.marriageInfo.cityOfMarriage || ""}
                onChange={(e) =>
                  onFieldChange(
                    "marriageInfo",
                    "cityOfMarriage",
                    "",
                    e.target.value,
                  )
                }
                placeholder="City of Marriage"
                className={`${INPUT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400`}
              />
              <select
                value={formData.marriageInfo.stateOfMarriage || ""}
                onChange={(e) =>
                  onFieldChange(
                    "marriageInfo",
                    "stateOfMarriage",
                    "",
                    e.target.value,
                  )
                }
                className={`${SELECT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
              >
                <option value="">Select State</option>
                {filingStateOptions?.map((state) => (
                  <option
                    key={state.state_id || state.id}
                    value={state.state_code || state.code}
                  >
                    {state.state_name || state.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Tax Status and Filing State */}
          <div className={`grid grid-cols-2 ${CARD_SPACING.grid}`}>
            <div>
              <label className={LABEL_BASE}>Tax Status</label>
              <select
                value={formData.taxInfo.filingStatus || ""}
                onChange={(e) =>
                  onFieldChange("filingStatus", e.target.value)
                }
                className={`${SELECT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
              >
                <option value="">Select...</option>
                {taxFilingStatusOptions?.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={LABEL_BASE}>Filing State</label>
              <select
                value={formData.taxInfo?.filingState || ""}
                onChange={(e) =>
                  onFieldChange("taxInfo", "filingState", "", e.target.value)
                }
                className={`${SELECT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
              >
                <option value="">Select...</option>
                {filingStateOptions?.map((state) => (
                  <option
                    key={state.state_id || state.id}
                    value={state.state_id || state.id}
                  >
                    {state.state_name || state.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Address Information */}
          <div>
            <label className={LABEL_BASE}>Primary Address</label>
            <div className={CARD_SPACING.row}>
              <div className={`grid grid-cols-2 ${CARD_SPACING.grid}`}>
                <input
                  type="text"
                  value={formData.address?.street || ""}
                  onChange={(e) =>
                    onFieldChange("address", "street", "", e.target.value)
                  }
                  placeholder="Street"
                  className={`${INPUT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400`}
                />
                <input
                  type="text"
                  value={formData.address?.unit || ""}
                  onChange={(e) =>
                    onFieldChange("address", "unit", "", e.target.value)
                  }
                  placeholder="Unit/Apt"
                  className={`${INPUT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400`}
                />
              </div>
              <div className={`grid grid-cols-3 ${CARD_SPACING.grid}`}>
                <input
                  type="text"
                  value={formData.address?.city || ""}
                  onChange={(e) =>
                    onFieldChange("address", "city", "", e.target.value)
                  }
                  placeholder="City"
                  className={`${INPUT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400`}
                />
                <select
                  value={formData.address?.state || ""}
                  onChange={(e) =>
                    onFieldChange("address", "state", "", e.target.value)
                  }
                  className={`${SELECT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                >
                  <option value="">Select State</option>
                  {filingStateOptions?.map((state) => (
                    <option
                      key={state.state_id || state.id}
                      value={state.state_code || state.code}
                    >
                      {state.state_name || state.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={formData.address?.zipCode || ""}
                  onChange={(e) =>
                    onFieldChange("address", "zipCode", "", e.target.value)
                  }
                  placeholder="ZIP Code"
                  className={`${INPUT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400`}
                />
              </div>
            </div>
          </div>

          {/* Mailing Address */}
          <div>
            <label className={LABEL_BASE}>Mailing Address</label>
            <div className={CARD_SPACING.row}>
              <div className={`grid grid-cols-2 ${CARD_SPACING.grid}`}>
                <input
                  type="text"
                  value={formData.address?.mailingAddress?.street || ""}
                  onChange={(e) =>
                    onFieldChange(
                      "address",
                      "mailingAddress",
                      "street",
                      e.target.value,
                    )
                  }
                  placeholder="Street"
                  className={`${INPUT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400`}
                />
                <input
                  type="text"
                  value={formData.address?.mailingAddress?.unit || ""}
                  onChange={(e) =>
                    onFieldChange(
                      "address",
                      "mailingAddress",
                      "unit",
                      e.target.value,
                    )
                  }
                  placeholder="Unit/Apt"
                  className={`${INPUT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400`}
                />
              </div>
              <div className={`grid grid-cols-3 ${CARD_SPACING.grid}`}>
                <input
                  type="text"
                  value={formData.address?.mailingAddress?.city || ""}
                  onChange={(e) =>
                    onFieldChange(
                      "address",
                      "mailingAddress",
                      "city",
                      e.target.value,
                    )
                  }
                  placeholder="City"
                  className={`${INPUT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400`}
                />
                <select
                  value={formData.address?.mailingAddress?.state || ""}
                  onChange={(e) =>
                    onFieldChange(
                      "address",
                      "mailingAddress",
                      "state",
                      e.target.value,
                    )
                  }
                  className={`${SELECT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white`}
                >
                  <option value="">Select State</option>
                  {filingStateOptions?.map((state) => (
                    <option
                      key={state.state_id || state.id}
                      value={state.state_code || state.code}
                    >
                      {state.state_name || state.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={formData.address?.mailingAddress?.zipCode || ""}
                  onChange={(e) =>
                    onFieldChange(
                      "address",
                      "mailingAddress",
                      "zipCode",
                      e.target.value,
                    )
                  }
                  placeholder="ZIP Code"
                  className={`${INPUT_BASE} dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400`}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
} 